/* Seam engine.

   ONE idea: a seam is a property of the BOUNDARY between two stacked sections, not of a
   section. So we render it as a real, in-flow block-level <div class="brave-seam"> inserted
   BETWEEN two adjacent panels whose resolved background colours DIFFER. The div is full width
   and SEAM_H tall; its background is a horizontally-repeating wavy SVG: the area above the wave
   is filled with the upper colour, the area below with the lower colour, plus two dotted "stitch"
   bead lines. Because the div sits between two known siblings and paints BOTH colours itself, it
   can never desync from its neighbours and there is no top/bottom asymmetry — the two reported
   straight-cut bugs cannot occur by construction.

   Being in normal flow, it needs NO z-index, NO isolation, NO negative-margin overlap, NO 100vw
   bleed measurement and NO resize re-measure (repeat-x + preserveAspectRatio='none' stretches it
   to any width; width:100% can never make a horizontal scrollbar). The footer (a separate group
   after <main>) is just the last panel, so the footer boundary has no special case.

   Isolated headers (collection banner / page hero / policy title) are NOT between-siblings
   boundaries, so they keep a small dedicated helper that paints a full-bleed cream band with a
   wavy bottom, reusing the same wave builder.

   Heritage polaroids live in brave-heritage.js. Scroll reveals live in brave-reveal.js.
   Fully reversible: remove the <script> tag in layout/theme.liquid. */
(function () {
  /* ---- tunables (one place) ---- */
  /* wave wavelength tile (background repeats every TILE_W px). Width is chosen by the
     'Seam wave strength' theme setting via <body data-seam-wave>: softer = wider tile (longer
     wavelength), harder = narrower. Read once at init; the customizer reloads on setting change. */
  var WAVE_W = { softer: 1350, default: 900, harder: 600 };
  var TILE_W = WAVE_W[(document.body && document.body.getAttribute('data-seam-wave')) || 'default'] || 900;
  var SEAM_H = 48;    // seam strip height (px) — must contain the wave + both bead lines
  var MID    = 24;    // wave centre line within the strip (== SEAM_H/2, the boundary)
  var AMP    = 10;    // wave amplitude (px) — LOCKED: keep the current subtle look
  var OFF    = 9;     // bead offset from the wave line (px)
  var SW     = 5;     // bead dot stroke width (px)
  var DASH   = '0.1 16';
  var CREAM  = '#FFECD3';
  var NEAR   = 24;    // per-channel colour-distance threshold for "same colour"

  /* 3 tileable wave variants (periodic over the tile so they repeat seamlessly; one per boundary
     index so two adjacent seams never look identical). */
  var VAR = [
    { k1: 2, k2: 1, m1: 0.66, m2: 0.34, p1: 0.0, p2: 0.9 },
    { k1: 3, k2: 1, m1: 0.60, m2: 0.40, p1: 0.7, p2: 0.0 },
    { k1: 2, k2: 3, m1: 0.74, m2: 0.26, p1: 0.3, p2: 1.7 }
  ];

  /* ---------- one-time CSS ---------- */
  function injectCSS() {
    if (document.getElementById('brave-seam-css')) return;
    var SIZE = TILE_W + 'px ' + SEAM_H + 'px';
    var CSS =
      /* full-bleed seams must never make a horizontal scrollbar (clip, not hidden, keeps the
         sticky header working) */
      'html{overflow-x:clip}body{overflow-x:clip}' +
      /* the in-flow divider: a normal block, no z-index/position games */
      '.brave-seam{display:block;width:100%;height:' + SEAM_H + 'px;margin:0;border:0;padding:0;' +
        'line-height:0;font-size:0;pointer-events:none;background-repeat:repeat-x;' +
        'background-position:center;background-size:' + SIZE + '}' +
      '@media print{.brave-seam{display:none!important}}' +
      /* trim the old breathing-room hacks: spacing is now the seam itself */
      '#MainContent{padding-bottom:0!important}' +

      /* ---- isolated header = full-bleed cream from the nav down to a wavy dotted bottom ---- */
      /* hide the old dry-brush cream plate (we paint our own full-bleed band) */
      '.collection-banner__band::before,.page__hero--cream::before,.shopify-policy__title::before{display:none!important}' +
      '.bps-fullhead-sec{padding-top:0!important;padding-bottom:0!important;margin:0!important;overflow:visible!important}' +
      '.bps-fullhead{position:relative!important;margin-top:0!important;margin-bottom:40px!important;isolation:isolate;overflow:visible!important;padding-top:34px!important;padding-bottom:64px!important}' +
      '.bps-fullhead .collection-banner__band,.page__hero.bps-fullhead{padding-left:0!important;padding-right:0!important;background:transparent!important}' +
      '.bps-fullhead .collection-banner__band,.bps-fullhead .page__hero-copy{padding-top:0!important;padding-bottom:0!important;margin-top:0!important;margin-bottom:0!important}' +
      '.bps-fullhead .page__lede,.bps-fullhead .collection-banner__meta,.bps-fullhead .page__hero-copy>*:last-child,.bps-fullhead .collection-banner__band>*:last-child{margin-bottom:0!important}' +
      /* the cream block + wavy seam are REAL children (immune to Brave pseudo rules), full-bleed
         via CSS (left:50%/-50vw), so NO per-frame JS measurement */
      '.bps-fullhead>.bps-bg,.bps-fullhead>.bps-seam{position:absolute!important;display:block!important;' +
        'left:50%!important;width:100vw!important;margin-left:-50vw!important;transform:none!important;' +
        '-webkit-mask:none!important;mask:none!important;border-radius:0!important;pointer-events:none!important}' +
      '.bps-fullhead>.bps-bg{top:0!important;bottom:auto!important;height:100%!important;background:var(--bg-cream,#FFECD3)!important;z-index:-2}' +
      '.bps-fullhead>.bps-seam{bottom:' + (-SEAM_H / 2) + 'px!important;top:auto!important;height:' + SEAM_H + 'px!important;' +
        'background-image:var(--bps-strip)!important;background-repeat:repeat-x!important;background-position:center!important;background-size:' + SIZE + '!important;z-index:-1}' +
      /* breadcrumb pill */
      '.bps-fullhead .breadcrumb,.bps-fullhead .breadcrumbs{display:inline-flex!important;align-items:center;margin:0 0 22px 0!important;padding:8px 18px!important;background:#fff;border:0!important;border-radius:999px;box-shadow:none!important;font-size:13px;line-height:1;width:auto!important}' +
      '.bps-fullhead .breadcrumb a,.bps-fullhead .breadcrumb span,.bps-fullhead .breadcrumbs a,.bps-fullhead .breadcrumbs span{font-size:13px}' +
      /* policy pages: align the column to the site gutter, left-align the title */
      '.shopify-policy__container{max-width:1200px!important;width:auto!important;margin-left:auto!important;margin-right:auto!important;padding-top:0!important;padding-left:24px!important;padding-right:24px!important;display:block!important;text-align:left!important;overflow:visible!important}' +
      '@media(max-width:749px){.shopify-policy__container{padding-left:16px!important;padding-right:16px!important}}' +
      '.shopify-policy__title.bps-fullhead{text-align:left!important;padding-left:0!important;padding-right:0!important}' +
      '.shopify-policy__title.bps-fullhead h1{padding-left:0!important;margin-left:0!important}' +
      '.shopify-policy__body{max-width:820px!important;margin-left:0!important;padding-left:0!important}';
    var st = document.createElement('style');
    st.id = 'brave-seam-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  /* ---------- colour helpers ---------- */
  function enc(s) { return encodeURIComponent(s).replace(/'/g, '%27').replace(/"/g, '%22'); }
  function opaque(c) { return c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent'; }
  var PAGEBG;
  function pageBg() {
    if (!PAGEBG) { var c = getComputedStyle(document.body).backgroundColor; PAGEBG = opaque(c) ? c : '#ffffff'; }
    return PAGEBG;
  }
  function ownBg(el) { var c = getComputedStyle(el).backgroundColor; return opaque(c) ? c : null; }
  function parseRGB(c) {
    if (!c) return [255, 255, 255];
    c = String(c).trim();
    if (c[0] === '#') { var h = c.slice(1); if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]; return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; }
    var m = c.match(/[\d.]+/g); return m ? [+m[0], +m[1], +m[2]] : [255, 255, 255];
  }
  function near(a, b) { var x = parseRGB(a), y = parseRGB(b); return (Math.abs(x[0] - y[0]) + Math.abs(x[1] - y[1]) + Math.abs(x[2] - y[2])) < NEAR; }

  /* a panel's resolved visible colour. NEVER returns null, NEVER skips — a white/transparent
     section resolves to the page colour and participates as a real neighbour. That single
     property fixes both reported bugs. Order: explicit opt-in -> own bg -> first child bg -> page. */
  function resolveColor(el) {
    if (!el || el.nodeType !== 1) return pageBg();
    var ds = el.getAttribute('data-seam-color');
    if (ds && ds.trim()) return ds.trim();
    var o = ownBg(el); if (o) return o;
    var inner = el.querySelector(':scope > section, :scope > div');
    if (inner) { var ic = ownBg(inner); if (ic) return ic; }
    return pageBg();
  }
  function colorOf(panel) {
    if (panel.classList.contains('shopify-section-group-footer-group')) {
      var f = panel.querySelector('.footer');
      return (f && ownBg(f)) || resolveColor(panel);
    }
    return resolveColor(panel);
  }

  /* ---------- the one wave + seam renderer ---------- */
  function wavePts(variant) {
    var v = VAR[((variant % 3) + 3) % 3], pts = [], N = 72;
    for (var i = 0; i <= N; i++) { var t = i / N; var y = MID + AMP * (v.m1 * Math.sin(2 * Math.PI * v.k1 * t + v.p1) + v.m2 * Math.sin(2 * Math.PI * v.k2 * t + v.p2)); pts.push([(TILE_W * t).toFixed(1), y.toFixed(2)]); }
    return pts;
  }
  function pathOf(pts, dy) { var d = 'M' + pts[0][0] + ' ' + (+pts[0][1] + dy).toFixed(2); for (var i = 1; i < pts.length; i++) d += ' L' + pts[i][0] + ' ' + (+pts[i][1] + dy).toFixed(2); return d; }
  var _cache = {};
  /* Two solid regions split by the wave (above-colour on top, below-colour on bottom) + two dotted
     bead lines colour-swapped for contrast. Because the strip is its OWN in-flow element it does
     not overlap the neighbours, so the regions are painted SOLID (not transparent tongues) and the
     div matches each neighbour as a flat colour band. */
  function buildSeam(aboveC, belowC, variant, noBeads) {
    var key = aboveC + '|' + belowC + '|' + variant + '|' + (noBeads ? 'nb' : 'b');
    if (_cache[key]) return _cache[key];
    var v = VAR[((variant % 3) + 3) % 3];
    var pts = wavePts(variant);
    var aboveArea = pathOf(pts, 0) + ' L' + TILE_W + ' 0 L0 0 Z';            // area ABOVE the wave
    var belowArea = pathOf(pts, 0) + ' L' + TILE_W + ' ' + SEAM_H + ' L0 ' + SEAM_H + ' Z'; // area BELOW
    var upBead = belowC;   // the upper bead takes the LOWER colour (contrast against the above region)
    var loBead = aboveC;   // the lower bead takes the UPPER colour
    /* Beads are discrete circles on x positions that EVENLY divide TILE_W, not a dashed stroke. A
       dash is phased by ARC length, and one tile's wave arc (~902px) is not an integer multiple of
       the dash period, so the dot at the tile seam mis-spaces — fusing on the top line and gapping
       on the half-offset bottom line (the reported joint bug). The wave is periodic over TILE_W
       (integer k), so circles on an even x-division tile seamlessly. The 0.25 / 0.75 phase keeps
       every dot clear of the x=0 / TILE_W seam so none is clipped on repeat. */
    function waveY(x) { var t = x / TILE_W; return MID + AMP * (v.m1 * Math.sin(2 * Math.PI * v.k1 * t + v.p1) + v.m2 * Math.sin(2 * Math.PI * v.k2 * t + v.p2)); }
    var N_BEADS = Math.round(TILE_W / 16.1), step = TILE_W / N_BEADS, R = SW / 2, beads = '';
    if (!noBeads) {
      for (var b = 0; b < N_BEADS; b++) {
        var xt = (b + 0.25) * step, xb = (b + 0.75) * step;
        beads += "<circle cx='" + xt.toFixed(2) + "' cy='" + (waveY(xt) - OFF).toFixed(2) + "' r='" + R + "' fill='" + upBead + "'/>";
        beads += "<circle cx='" + xb.toFixed(2) + "' cy='" + (waveY(xb) + OFF).toFixed(2) + "' r='" + R + "' fill='" + loBead + "'/>";
      }
    }
    var svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 " + TILE_W + ' ' + SEAM_H + "' preserveAspectRatio='none'>" +
      "<path d='" + aboveArea + "' fill='" + aboveC + "'/>" +
      "<path d='" + belowArea + "' fill='" + belowC + "'/>" +
      beads +
      "</svg>";
    var url = 'url("data:image/svg+xml,' + enc(svg) + '")';
    _cache[key] = url;
    return url;
  }

  /* ---------- the reconciler (idempotent) ---------- */
  function isSeam(el) { return !!(el && el.nodeType === 1 && el.classList && el.classList.contains('brave-seam')); }
  function isEmpty(el) { return el.getBoundingClientRect().height < 2; }
  function panels() {
    var list = [].slice.call(document.querySelectorAll('#MainContent > .shopify-section'))
      .filter(function (s) { return !isSeam(s) && !isEmpty(s); });
    /* policy pages render Shopify's bare .shopify-policy__container straight into #MainContent
       (no .shopify-section wrapper), so without this it is invisible to the reconciler and the
       footer boundary has no upper panel -> the footer seam goes missing (straight cut). */
    var policy = document.querySelector('#MainContent > .shopify-policy__container');
    if (policy && !isEmpty(policy)) list.push(policy);
    var footer = document.querySelector('.shopify-section-group-footer-group');
    if (footer && !isEmpty(footer)) list.push(footer);
    return list;
  }

  function buildSectionSeams() {
    var P = panels(), keep = [];
    for (var i = 1; i < P.length; i++) {
      var above = P[i - 1], below = P[i];
      var ca = colorOf(above), cb = colorOf(below);
      var existing = above.nextElementSibling; if (!isSeam(existing)) existing = null;
      if (near(ca, cb)) { if (existing) existing.parentNode.removeChild(existing); continue; }
      var variant = i % 3;
      /* per-seam beads: the section ABOVE the joint opts out via a hidden [data-no-seam-beads]
         marker (the 'Hide seam beads below this section' setting). Wave + colours stay. */
      var noBeads = !!(above.querySelector && above.querySelector('[data-no-seam-beads]'));
      var nb = noBeads ? '1' : '0';
      if (existing && existing.getAttribute('data-ab') === ca && existing.getAttribute('data-be') === cb && existing.getAttribute('data-v') === String(variant) && existing.getAttribute('data-nb') === nb) { keep.push(existing); continue; }
      var seam = existing || document.createElement('div');
      seam.className = 'brave-seam';
      seam.setAttribute('aria-hidden', 'true');
      seam.setAttribute('data-ab', ca); seam.setAttribute('data-be', cb); seam.setAttribute('data-v', String(variant)); seam.setAttribute('data-nb', nb);
      seam.style.backgroundImage = buildSeam(ca, cb, variant, noBeads);
      if (!existing) above.after(seam);
      keep.push(seam);
    }
    /* sweep orphan between-section seams (a boundary that became same-colour, or a removed
       section). Header seams use a different class, so they are untouched. */
    [].slice.call(document.querySelectorAll('#MainContent > .brave-seam')).forEach(function (s) {
      if (keep.indexOf(s) === -1) s.parentNode.removeChild(s);
    });
  }

  /* ---------- isolated headers (cream full-bleed + wavy bottom) ---------- */
  function decorateHead(H, belowColor, variant) {
    H.classList.add('bps-fullhead');
    H.style.setProperty('--bps-strip', buildSeam(CREAM, belowColor || pageBg(), variant || 0));
    if (!H.querySelector(':scope > .bps-bg')) { var bg = document.createElement('i'); bg.className = 'bps-bg'; bg.setAttribute('aria-hidden', 'true'); H.insertBefore(bg, H.firstChild); }
    if (!H.querySelector(':scope > .bps-seam')) { var sm = document.createElement('i'); sm.className = 'bps-seam'; sm.setAttribute('aria-hidden', 'true'); H.insertBefore(sm, H.firstChild); }
  }
  function applyHeaders() {
    [].slice.call(document.querySelectorAll('.collection-banner')).forEach(function (H) {
      var sec = H.closest('.shopify-section'); if (sec) sec.classList.add('bps-fullhead-sec');
      decorateHead(H, resolveColor(sec ? sec.nextElementSibling : H.nextElementSibling), 0);
    });
    [].slice.call(document.querySelectorAll('.page__hero--cream')).forEach(function (H) {
      var prev = H.previousElementSibling;
      if (prev && /breadcrumb/i.test(prev.className || '') && !H.querySelector('.breadcrumb,.breadcrumbs')) H.insertBefore(prev, H.firstChild);
      var sec = H.closest('.shopify-section'); if (sec) sec.classList.add('bps-fullhead-sec');
      decorateHead(H, resolveColor(H.nextElementSibling || (sec ? sec.nextElementSibling : null)), 1);
    });
    [].slice.call(document.querySelectorAll('.shopify-policy__title')).forEach(function (H) {
      decorateHead(H, resolveColor(H.nextElementSibling || H.parentElement), 2);
    });
  }

  /* ---------- run ---------- */
  function run() { try { injectCSS(); applyHeaders(); buildSectionSeams(); } catch (e) {} }
  run();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('load', run);
  var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(run, 150); }, { passive: true });
  /* Shopify theme editor: rebuild on add/remove/reorder/select (debounced + the no-op guard keep it cheap) */
  var et; function onEdit() { clearTimeout(et); et = setTimeout(run, 60); }
  document.addEventListener('shopify:section:load', onEdit);
  document.addEventListener('shopify:section:unload', onEdit);
  document.addEventListener('shopify:section:reorder', onEdit);
  document.addEventListener('shopify:block:select', onEdit);
  setTimeout(run, 400); setTimeout(run, 1200);
  window.BraveSeam = { run: run };
})();
